import {type RefObject, useCallback, useEffect, useState} from "react";
import {useConsent} from "consent/useConsent";
import {SITE_FOOTER_CLEARANCE_PROPERTY} from "nav/SiteFooter.styles";
import {appScroller} from "utils/appScroller";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {backToTopClearance, backToTopScrollBehavior, showsBackToTop} from "./backToTop";

/**
 * Binds `pricing/backToTop` to the page (kitecorp/transferify-ui#4043): reads the app scroller and the window, and asks
 * the rule.
 *
 * `visible` starts false and only an effect can raise it, so the button is in neither the pre-rendered HTML nor the first
 * client render, whatever `useConsent` reads from this browser's storage on that render.
 *
 * @param focusTarget where keyboard focus goes when the reader goes back up.
 */
export function useBackToTop(focusTarget: RefObject<HTMLElement | null>) {
	const {consent} = useConsent();
	// The same answer the banner hides on and the cookie settings button shows on, not a second reading of it.
	const bannerUp = !consent.decided;
	const [visible, setVisible] = useState(false);
	const [button, setButton] = useState<HTMLButtonElement | null>(null);
	const [clearance, setClearance] = useState(0);

	// Depends on the banner, so an answer reads the page again and shows the button without waiting for a scroll.
	useEffect(() => {
		const scroller = appScroller();
		if (scroller === null) return undefined;
		const read = () => setVisible(showsBackToTop({
			scrollTop: scroller.scrollTop,
			scrollerHeight: scroller.clientHeight,
			viewportWidth: window.innerWidth,
			bannerUp,
		}));
		read();
		scroller.addEventListener("scroll", read, {passive: true});
		window.addEventListener("resize", read);
		return () => {
			scroller.removeEventListener("scroll", read);
			window.removeEventListener("resize", read);
		};
	}, [bannerUp]);

	// Before paint, in the frame the button first paints in. Published only while the button is on the page, so the
	// footer of a wider window, or of a reader back at the top, keeps its own height.
	useIsomorphicLayoutEffect(() => {
		if (button === null) return undefined;
		const root = document.documentElement;
		const publish = () => {
			const next = backToTopClearance(window.innerHeight, button.getBoundingClientRect().top);
			setClearance(next);
			root.style.setProperty(SITE_FOOTER_CLEARANCE_PROPERTY, `${next}px`);
		};
		publish();
		// Fixed, so only the window moves it: its inset tightens below the phone breakpoint.
		window.addEventListener("resize", publish);
		return () => {
			window.removeEventListener("resize", publish);
			root.style.removeProperty(SITE_FOOTER_CLEARANCE_PROPERTY);
		};
	}, [button]);

	// Focus first, while the button still exists to hand it over: the scroll hides the button, and focus on a removed
	// element falls back to the document. `preventScroll`, because the browser's own jump to the target would replace
	// the smooth trip below.
	const goToTop = useCallback(() => {
		focusTarget.current?.focus({preventScroll: true});
		appScroller()?.scrollTo({
			top: 0,
			behavior: backToTopScrollBehavior(window.matchMedia("(prefers-reduced-motion: reduce)").matches),
		});
	}, [focusTarget]);

	return {visible, clearance, setButton, goToTop};
}
